import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Play, X } from "lucide-react";
import { format } from "date-fns";
import { RULE_KINDS, KIND_META, DEFAULT_CONFIG, validateConfig, configHasPlaceholder, type RuleKind } from "./kindConfig";
import { WatchRuleKindForm } from "./WatchRuleKindForm";

export default function WatchRuleEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: canManage } = useResearchCapability("manage");

  const [kind, setKind] = useState<RuleKind | null>(null);
  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<"internal_only" | "partners">("internal_only");
  const [config, setConfig] = useState<any>(null);
  const [cadenceMinutes, setCadenceMinutes] = useState<number>(60);
  const [saving, setSaving] = useState(false);
  const [dryRun, setDryRun] = useState<any>(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);


  const { data: existing } = useQuery({
    queryKey: ["research_watch_rule", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("research_watch_rules").select("*").eq("id", id!).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !isNew,
  });

  const { data: taxonomy } = useQuery({
    queryKey: ["research_taxonomy_values_active"],
    queryFn: async () => {
      const { data, error } = await supabase.from("research_taxonomy_values").select("axis,value").eq("is_active", true).order("axis").order("value");
      if (error) throw error;
      return data as { axis: string; value: string }[];
    },
  });

  useEffect(() => {
    if (existing) {
      setKind(existing.rule_kind as RuleKind);
      setName(existing.name);
      setEnabled(existing.enabled);
      setTags(Array.isArray(existing.default_tags) ? existing.default_tags as string[] : []);
      setVisibility((existing.default_visibility as any) ?? "internal_only");
      setConfig(existing.config ?? DEFAULT_CONFIG[existing.rule_kind as RuleKind]);
      setCadenceMinutes((existing as any).cadence_minutes ?? 60);
    }
  }, [existing]);


  const meta = kind ? KIND_META[kind] : null;
  const hasPlaceholder = useMemo(() => configHasPlaceholder(config), [config]);

  if (!canManage) return <p className="text-sm text-muted-foreground">Requires manage capability.</p>;

  // Step 1: pick a kind (new only, no kind selected)
  if (isNew && !kind) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm"><Link to="/partner/research/watch-rules"><ArrowLeft className="h-4 w-4 mr-1" />Back</Link></Button>
        <div>
          <h2 className="text-xl font-semibold">New watch rule</h2>
          <p className="text-sm text-muted-foreground">Pick a rule kind to start.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {RULE_KINDS.map((k) => {
            const Icon = k.icon;
            return (
              <button key={k.kind} onClick={() => { setKind(k.kind); setConfig(DEFAULT_CONFIG[k.kind]); }}
                className="text-left">
                <Card className="hover:border-primary transition-colors h-full">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-primary" /><h3 className="font-semibold">{k.label}</h3></div>
                    <p className="text-xs text-muted-foreground">{k.desc}</p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (!isNew && !existing) return <p className="text-sm text-muted-foreground">Loading rule…</p>;
  if (!kind || !meta) return null;

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    const err = validateConfig(kind, config);
    if (err) { toast.error(err); return; }
    setSaving(true);
    const cadence = Math.max(1, Math.min(10080, Math.round(Number(cadenceMinutes) || 60)));
    const payload = {
      rule_kind: kind, name: name.trim(), config, enabled,
      default_tags: tags, default_visibility: visibility,
      cadence_minutes: cadence,
    };

    const { data, error } = isNew
      ? await supabase.from("research_watch_rules").insert(payload).select("id").single()
      : await supabase.from("research_watch_rules").update(payload).eq("id", id!).select("id").single();
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Saved");
    qc.invalidateQueries({ queryKey: ["research_watch_rules"] });
    if (isNew && data?.id) navigate(`/partner/research/watch-rules/${data.id}`, { replace: true });
  };

  const handleDryRun = async () => {
    if (isNew || !id) { toast.info("Save the rule first"); return; }
    if (!meta.supportsRunNow) { toast.info("Dry-run coming soon for this kind"); return; }
    setDryRunLoading(true);
    setDryRun(null);
    const { data, error } = await supabase.functions.invoke(meta.ingestFn, { body: { watch_rule_id: id, dry_run: true } });
    setDryRunLoading(false);
    if (error) { toast.error(error.message); setDryRun({ error: error.message }); }
    else { setDryRun(data); }
  };

  const Icon = meta.icon;

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm"><Link to="/partner/research/watch-rules"><ArrowLeft className="h-4 w-4 mr-1" />Back to rules</Link></Button>

      {hasPlaceholder && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
            <span>This rule's config contains a <code>PLACEHOLDER_</code> value. Replace it before enabling.</span>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="gap-1"><Icon className="h-3 w-3" />{meta.label}</Badge>
              <span className="text-xs text-muted-foreground">{meta.desc}</span>
            </div>

            <div>
              <Label className="text-xs">Name</Label>
              <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Human-readable rule name" />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Switch checked={enabled} onCheckedChange={setEnabled} /> Enabled
            </label>

            <div>
              <Label className="text-xs">Cadence (minutes between runs)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min={1} max={10080} className="w-32"
                  value={cadenceMinutes}
                  onChange={(e) => setCadenceMinutes(parseInt(e.target.value || "0", 10))} />
                <div className="flex flex-wrap gap-1">
                  {[15, 30, 60, 240, 720, 1440].map((m) => (
                    <button key={m} type="button" onClick={() => setCadenceMinutes(m)}
                      className="px-2 py-0.5 text-xs rounded border bg-background hover:bg-muted">
                      {m < 60 ? `${m}m` : m === 1440 ? "1d" : `${m / 60}h`}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                The job runner currently ticks on a fixed cron; this value is the target cadence and will be honoured once the runner respects per-rule scheduling.
              </p>
            </div>


            <div>
              <Label className="text-xs">Default tags</Label>
              <TagsPicker selected={tags} setSelected={setTags} options={taxonomy ?? []} />
            </div>

            <div>
              <Label className="text-xs">Default visibility</Label>
              <RadioGroup className="mt-2 flex gap-4" value={visibility} onValueChange={(v) => setVisibility(v as any)}>
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="internal_only" /> Internal only</label>
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="partners" /> Partners</label>
              </RadioGroup>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-sm mb-2">{meta.label} configuration</h3>
              {config !== null && <WatchRuleKindForm kind={kind} config={config} onChange={setConfig} />}
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
              <Button variant="outline" asChild><Link to="/partner/research/watch-rules">Cancel</Link></Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="font-semibold text-sm">Test</h3>
              <Button variant="outline" className="w-full" disabled={dryRunLoading || isNew || !meta.supportsRunNow}
                onClick={handleDryRun}>
                <Play className="h-3 w-3 mr-1" /> {dryRunLoading ? "Running…" : "Dry-run"}
              </Button>
              {!meta.supportsRunNow && <p className="text-xs text-muted-foreground">Single-rule trigger not yet supported by the backend for this kind.</p>}
              {dryRun && (
                <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-72">{JSON.stringify(dryRun, null, 2)}</pre>
              )}
            </CardContent>
          </Card>

          {!isNew && existing && (
            <Card>
              <CardContent className="p-4 space-y-2">
                <h3 className="font-semibold text-sm">Last run</h3>
                <p className="text-xs text-muted-foreground">
                  {existing.last_run_at ? format(new Date(existing.last_run_at), "dd/MM/yyyy HH:mm") : "Never"}
                </p>
                {existing.last_result && (
                  <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-48">{JSON.stringify(existing.last_result, null, 2)}</pre>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4 text-xs text-muted-foreground space-y-1">
              <p><strong className="text-foreground">Cadence:</strong> set per connector at the cron level — cannot be changed per rule.</p>
              <p><strong className="text-foreground">Connector status:</strong> OAuth health panel pending backend helper (<code>get_research_oauth_status</code>).</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function TagsPicker({ selected, setSelected, options }: { selected: string[]; setSelected: (v: string[]) => void; options: { axis: string; value: string }[] }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const lq = q.toLowerCase();
    return options.filter((o) => !selected.includes(o.value) && (o.value.toLowerCase().includes(lq) || o.axis.toLowerCase().includes(lq))).slice(0, 30);
  }, [options, q, selected]);
  return (
    <div className="mt-1 space-y-2">
      <div className="flex flex-wrap gap-1">
        {selected.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1">
            {v}
            <button type="button" onClick={() => setSelected(selected.filter((x) => x !== v))}><X className="h-3 w-3" /></button>
          </Badge>
        ))}
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search taxonomy…" />
      {q && filtered.length > 0 && (
        <div className="border rounded max-h-48 overflow-auto">
          {filtered.map((o) => (
            <button key={o.value} type="button" onClick={() => { setSelected([...selected, o.value]); setQ(""); }}
              className="w-full text-left px-2 py-1 text-xs hover:bg-muted flex justify-between">
              <span>{o.value}</span><span className="text-muted-foreground">{o.axis}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
